import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImageOff, MessageSquareWarning, Scale } from "lucide-react";
import { useApi, useAuth, usePermission } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import {
  Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  EmptyState, Field, InlineAlert, NativeSelect, PageHeader, Skeleton, Tabs, TabsList, TabsTrigger, Textarea, cn, useSave,
} from "@ncp/ui";

interface ClassificationEntry { at: string; by: string; side: "Department" | "Net0"; kind: "Classified" | "Upheld" | "Proposed"; class: string; section?: string | null; noErrorArea?: string | null; reasons: string }

interface FeedbackRow {
  id: string;
  createdAt: string;
  reporterName: string;
  app: string;
  path: string;
  caseNumber?: string | null;
  category: string;
  description: string;
  locale: string;
  viewport?: string | null;
  appVersion?: string | null;
  hasScreenshot: boolean;
  status: string;
  resolutionNote?: string | null;
  section?: string | null;
  defectClass?: string | null;
  noErrorArea?: string | null;
  classification?: ClassificationEntry[] | null;
  openProposal?: ClassificationEntry | null;
}

interface SectionAcceptance {
  section: string; reports: number; critical: number; major: number; minor: number; openCritical: number; openMajor: number; openMinor: number;
  noErrorAreaOpen: number; noErrorAreaCorrected: number; unclassified: number; disagreements: number; verdict: "Met" | "NotMet" | "Pending"; reasons: string[]; reasonsAr: string[];
}

interface AcceptanceReport { sections: SectionAcceptance[]; maxMinor: number; classes: { key: string; en: string; ar: string }[] }

const STATUSES = ["New", "InProgress", "Resolved", "Dismissed"] as const;
const CLASSES = ["Critical", "Major", "Minor"] as const;
const SECTIONS = ["EC", "AC", "WB", "EX", "EA", "GR", "LE", "RQ", "DB", "DW", "ST", "AD", "PW"] as const;
const AREAS = ["Threshold", "Deadline", "Fee"] as const;
/** The Department's officers who classify defects (condition 1.7); the API applies the same rule. */
const DEPARTMENT_ROLES = ["Director", "HeadOfComplaints", "AssistantUndersecretary", "Undersecretary"];

const classTone = (c?: string | null) => (c === "Critical" ? "danger" : c === "Major" ? "warning" : c === "Minor" ? "info" : "outline") as "danger" | "warning" | "info" | "outline";

function Screenshot({ id }: { id: string }) {
  const api = useApi();
  const { token } = useAuth();
  const image = useQuery({
    queryKey: ["feedback-shot", id],
    queryFn: async () => {
      const res = await fetch(`${api.baseUrl}/api/platform/feedback/${id}/screenshot`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(String(res.status));
      return URL.createObjectURL(await res.blob());
    },
    staleTime: Infinity,
  });
  if (image.isPending) return <Skeleton className="h-40 w-full" />;
  if (!image.data) return <div className="flex h-40 items-center justify-center rounded-lg bg-sand-100 text-muted-foreground"><ImageOff className="size-5" /></div>;
  return <a href={image.data} target="_blank" rel="noreferrer"><img src={image.data} alt="" className="max-h-64 w-full rounded-lg border border-sand-200 object-cover object-top" /></a>;
}

/** Condition 1.8: each section against the acceptance threshold, on the defects reported against it by the Department's class. */
function AcceptancePanel({ report }: { report: AcceptanceReport }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <Card className="mb-5">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><Scale className="size-4 text-aegold-700" /> {t("admin:feedback.acceptance.title")}</CardTitle>
        <CardDescription>{t("admin:feedback.acceptance.hint", { max: report.maxMinor })}</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm" data-testid="defect-acceptance">
          <thead className="text-start text-xs text-muted-foreground">
            <tr>
              <th className="py-1.5 text-start font-medium">{t("admin:feedback.acceptance.section")}</th>
              {CLASSES.map((c) => <th key={c} className="py-1.5 text-end font-medium">{t(`admin:feedback.classes.${c}`)}</th>)}
              <th className="py-1.5 text-end font-medium">{t("admin:feedback.acceptance.noErrorArea")}</th>
              <th className="py-1.5 text-end font-medium">{t("admin:feedback.acceptance.unclassified")}</th>
              <th className="py-1.5 text-start font-medium ps-4">{t("admin:feedback.acceptance.verdict")}</th>
            </tr>
          </thead>
          <tbody>
            {report.sections.map((s) => (
              <tr key={s.section} className="border-t border-sand-100 align-top">
                <td className="py-1.5 font-medium">{t(`admin:feedback.sections.${s.section}`)}</td>
                <td className="py-1.5 text-end tabular">{s.openCritical}/{s.critical}</td>
                <td className="py-1.5 text-end tabular">{s.openMajor}/{s.major}</td>
                <td className="py-1.5 text-end tabular">{s.openMinor}/{s.minor}</td>
                <td className="py-1.5 text-end tabular">{s.noErrorAreaOpen}</td>
                <td className="py-1.5 text-end tabular">{s.unclassified}</td>
                <td className="py-1.5 ps-4">
                  <Badge size="sm" variant={s.verdict === "Met" ? "success" : s.verdict === "Pending" ? "outline" : "danger"}>{t(`admin:feedback.acceptance.verdicts.${s.verdict}`)}</Badge>
                  {(locale === "ar" ? s.reasonsAr : s.reasons).map((r) => <p key={r} className="mt-0.5 text-xs text-muted-foreground">{r}</p>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-muted-foreground">{t("admin:feedback.acceptance.legend")}</p>
      </CardContent>
    </Card>
  );
}

/** The Department's class of a defect, or Net0's proposal of another class (condition 1.7). */
function ClassDialog({ row, mode, definitions, onClose }: { row: FeedbackRow; mode: "classify" | "propose"; definitions: AcceptanceReport["classes"]; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const [cls, setCls] = useState<string>(mode === "classify" ? row.defectClass ?? "" : "");
  const [section, setSection] = useState<string>(row.section ?? "PW");
  const [area, setArea] = useState<string>(row.noErrorArea ?? "");
  const [reasons, setReasons] = useState("");
  const save = useSave({
    mutationFn: () => mode === "classify"
      ? api.post<FeedbackRow>(`/api/platform/feedback/${row.id}/class`, { class: cls, section, noErrorArea: area || null, reasons })
      : api.post<FeedbackRow>(`/api/platform/feedback/${row.id}/proposal`, { class: cls, reasons }),
    expect: "record",
    saved: mode === "classify" ? t("admin:feedback.classify.saved") : t("admin:feedback.propose.saved"),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["feedback"] }); onClose(); },
  });
  const min = mode === "classify" ? 10 : 20;
  const definition = definitions.find((d) => d.key === cls);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t(mode === "classify" ? "admin:feedback.classify.title" : "admin:feedback.propose.title")}</DialogTitle>
          <DialogDescription>{t(mode === "classify" ? "admin:feedback.classify.hint" : "admin:feedback.propose.hint", { current: row.defectClass ? t(`admin:feedback.classes.${row.defectClass}`) : "" })}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t("admin:feedback.classify.class")} required>{(id) => (
            <NativeSelect id={id} value={cls} onChange={(e) => setCls(e.target.value)}>
              <option value="">—</option>
              {CLASSES.filter((c) => mode === "classify" || c !== row.defectClass).map((c) => <option key={c} value={c}>{t(`admin:feedback.classes.${c}`)}</option>)}
            </NativeSelect>
          )}</Field>
          {mode === "classify" ? (
            <Field label={t("admin:feedback.acceptance.section")} required>{(id) => (
              <NativeSelect id={id} value={section} onChange={(e) => setSection(e.target.value)}>
                {SECTIONS.map((s) => <option key={s} value={s}>{t(`admin:feedback.sections.${s}`)}</option>)}
              </NativeSelect>
            )}</Field>
          ) : null}
          {definition ? <p className="text-xs text-muted-foreground md:col-span-2">{locale === "ar" ? definition.ar : definition.en}</p> : null}
          {mode === "classify" ? (
            <Field label={t("admin:feedback.classify.area")} hint={t("admin:feedback.classify.areaHint")} className="md:col-span-2">{(id) => (
              <NativeSelect id={id} value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">{t("admin:feedback.classify.noArea")}</option>
                {AREAS.map((a) => <option key={a} value={a}>{t(`admin:feedback.areas.${a}`)}</option>)}
              </NativeSelect>
            )}</Field>
          ) : null}
          <Field label={t("admin:feedback.classify.reasons")} required className="md:col-span-2">{(id) => <Textarea id={id} rows={4} value={reasons} onChange={(e) => setReasons(e.target.value)} draft={`feedback:${row.id}:${mode}:reasons`} dir="auto" />}</Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("actions.cancel")}</Button>
          <Button variant="gold" disabled={!cls || reasons.trim().length < min} loading={save.isPending} onClick={() => save.mutate()}>{t(mode === "classify" ? "admin:feedback.classify.submit" : "admin:feedback.propose.submit")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Issues users reported from the screen they were on ("Report an issue" in every app's header), with the Department's class of each defect. */
export function FeedbackPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { hasRole, user } = useAuth();
  const api = useApi();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("New");
  const [section, setSection] = useState<string>("");
  const [dialog, setDialog] = useState<{ row: FeedbackRow; mode: "classify" | "propose" } | null>(null);
  const isDepartment = (user?.roles ?? []).some((r) => DEPARTMENT_ROLES.includes(r));
  const isNet0 = hasRole("Admin");
  // The status of a report is the platform team's triage, kept with the audit readers; a classifier sees the reports and classifies them.
  const { can } = usePermission();
  const mayTriage = can("platform.audit.read");
  const query = new URLSearchParams();
  if (status) query.set("status", status);
  if (section) query.set("section", section);
  const list = useQuery({ queryKey: ["feedback", status, section], queryFn: () => api.get<FeedbackRow[]>(`/api/platform/feedback?${query.toString()}`) });
  const acceptance = useQuery({ queryKey: ["feedback", "acceptance"], queryFn: () => api.get<AcceptanceReport>("/api/platform/feedback/acceptance") });
  const update = useSave({
    mutationFn: ({ id, next }: { id: string; next: string }) => api.post(`/api/platform/feedback/${id}/status`, { status: next }),
    saved: t("admin:feedback.updated"),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["feedback"] }); },
  });

  return (
    <div>
      <PageHeader title={t("admin:feedback.title")} description={t("admin:feedback.subtitle")} />
      {acceptance.data ? <AcceptancePanel report={acceptance.data} /> : null}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            {STATUSES.map((s) => <TabsTrigger key={s} value={s}>{t(`admin:feedback.statuses.${s}`)}</TabsTrigger>)}
            <TabsTrigger value="">{t("admin:feedback.all")}</TabsTrigger>
          </TabsList>
        </Tabs>
        <NativeSelect value={section} onChange={(e) => setSection(e.target.value)} className="h-9 w-56" aria-label={t("admin:feedback.acceptance.section")}>
          <option value="">{t("admin:feedback.allSections")}</option>
          {SECTIONS.map((s) => <option key={s} value={s}>{t(`admin:feedback.sections.${s}`)}</option>)}
        </NativeSelect>
      </div>
      {list.isPending ? <Skeleton className="h-40 w-full" /> : (list.data ?? []).length === 0 ? (
        <EmptyState icon={MessageSquareWarning} title={t("admin:feedback.empty")} />
      ) : (
        <div className="space-y-3">
          {list.data!.map((f) => (
            <Card key={f.id}>
              <CardContent className="grid gap-4 p-5 lg:grid-cols-[1fr_320px]">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge size="sm" variant={classTone(f.defectClass)} data-testid="defect-class">{f.defectClass ? t(`admin:feedback.classes.${f.defectClass}`) : t("admin:feedback.unclassified")}</Badge>
                    {f.noErrorArea ? <Badge size="sm" variant="danger">{t(`admin:feedback.areas.${f.noErrorArea}`)}</Badge> : null}
                    {f.section ? <Badge size="sm" variant="outline">{t(`admin:feedback.sections.${f.section}`)}</Badge> : null}
                    <Badge size="sm" variant="outline">{t(`admin:feedback.categories.${f.category}`, { defaultValue: f.category })}</Badge>
                    <Badge size="sm" variant={f.status === "New" ? "warning" : f.status === "Resolved" ? "success" : "outline"}>{t(`admin:feedback.statuses.${f.status}`, { defaultValue: f.status })}</Badge>
                    <span>{formatDateTime(f.createdAt, locale)}</span>
                    <span>· {t("admin:feedback.reporter")}: {f.reporterName}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed" dir="auto">{f.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("admin:feedback.page")}: <bdi className="font-mono">{f.app}{f.path}</bdi>
                    {f.caseNumber ? <> · <bdi>{f.caseNumber}</bdi></> : null}
                    {f.appVersion ? <> · <bdi>{f.appVersion}</bdi></> : null}
                    {f.viewport ? <> · <bdi>{f.viewport}</bdi></> : null}
                  </p>
                  {f.openProposal ? (
                    <InlineAlert tone="warning" title={t("admin:feedback.disagreement", { proposed: t(`admin:feedback.classes.${f.openProposal.class}`), applies: f.defectClass ? t(`admin:feedback.classes.${f.defectClass}`) : "" })}>
                      <span dir="auto">{f.openProposal.reasons}</span>
                    </InlineAlert>
                  ) : null}
                  {(f.classification ?? []).length > 0 ? (
                    <ul className="space-y-0.5 text-xs text-muted-foreground" data-testid="defect-history">
                      {f.classification!.map((h) => (
                        <li key={`${h.at}-${h.kind}`}>{formatDateTime(h.at, locale)} · {t(`admin:feedback.history.${h.kind}`, { by: h.by, cls: t(`admin:feedback.classes.${h.class}`) })} – <span dir="auto">{h.reasons}</span></li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {isDepartment ? <Button size="sm" variant="gold" onClick={() => setDialog({ row: f, mode: "classify" })}>{t(f.defectClass ? "admin:feedback.classify.change" : "admin:feedback.classify.action")}</Button> : null}
                    {isNet0 && f.defectClass ? <Button size="sm" variant="outline" onClick={() => setDialog({ row: f, mode: "propose" })}>{t("admin:feedback.propose.action")}</Button> : null}
                    {!mayTriage ? null : STATUSES.filter((s) => s !== f.status).map((s) => (
                      <Button key={s} size="sm" variant="outline" loading={update.isPending && update.variables?.id === f.id && update.variables.next === s} onClick={() => update.mutate({ id: f.id, next: s })}>
                        {t("admin:feedback.setStatus", { status: t(`admin:feedback.statuses.${s}`) })}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className={cn(!f.hasScreenshot && "hidden lg:block")}>{f.hasScreenshot ? <Screenshot id={f.id} /> : null}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {dialog ? <ClassDialog row={dialog.row} mode={dialog.mode} definitions={acceptance.data?.classes ?? []} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
